import { motion } from 'framer-motion';
import { BookOpen, CheckCircle, Shield, HelpCircle, ArrowRight, Video } from 'lucide-react';
import { BookCallModal } from './BookCallModal';

// Official Google Meet icon
const GoogleMeetIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 87.5 72" xmlns="http://www.w3.org/2000/svg">
    <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z"/>
    <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z"/>
    <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z"/>
    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z"/>
    <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z"/>
    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z"/>
  </svg>
);

const CALL_BENEFITS = [
  {
    icon: BookOpen,
    title: 'Understand',
    description: 'How the system works',
    color: '#7c3aed',
  },
  {
    icon: CheckCircle,
    title: 'Verify',
    description: 'We run the tester live on screen share',
    color: '#10B981',
  },
  {
    icon: Shield,
    title: 'Plan',
    description: 'Your account, platform and risk',
    color: '#06b6d4',
  },
  {
    icon: HelpCircle,
    title: 'Clear your queries',
    description: 'Any question, answered directly',
    color: '#F59E0B',
  },
];

export const BookCallSection = ({ onBookCall }) => {
  return (
    <section className="py-16 md:py-20 relative overflow-hidden" data-testid="book-call-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#7c3aed]/5 via-transparent to-[#06b6d4]/5" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#7c3aed]/30 mb-4">
            <GoogleMeetIcon className="w-4 h-4" />
            <span className="text-[#7c3aed] text-xs font-mono">FREE CONSULTATION</span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
            Book a Google Meet with Sahil
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            A free 20-minute call with the founder, before you decide.
          </p>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left side: Benefits cards */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {CALL_BENEFITS.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${benefit.color}15` }}
                >
                  <benefit.icon className="w-6 h-6" style={{ color: benefit.color }} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white mb-1 group-hover:text-[#7c3aed] transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-white/50 text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            ))}

            {/* Trust note */}
            <div className="flex items-center gap-3 pt-4">
              <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10">
                <img 
                  src="/founder.jpg" 
                  alt="Sahil Aswani" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Sahil Aswani</p>
                <p className="text-white/40 text-xs">Founder, 8+ years trading experience</p>
              </div>
            </div>
          </motion.div>

          {/* Right side: Embedded form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Google Meet header */}
            <div className="flex items-center gap-2 mb-4">
              <GoogleMeetIcon className="w-5 h-5" />
              <span className="text-white/60 text-sm">on Google Meet</span>
            </div>

            {/* Embedded booking form preview / CTA */}
            <div className="glass-strong rounded-2xl p-6 border border-[#7c3aed]/20">
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7c3aed]/20 to-[#06b6d4]/20 flex items-center justify-center mx-auto mb-6">
                  <Video className="w-8 h-8 text-[#7c3aed]" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">
                  Free 20-Minute Consultation
                </h3>
                <p className="text-white/50 text-sm mb-6 max-w-sm mx-auto">
                  Answer a few quick questions, then pick a time that works for you.
                </p>
                <motion.button
                  onClick={onBookCall}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full py-4 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] text-white font-heading font-semibold shadow-lg shadow-[#7c3aed]/30 hover:shadow-[#7c3aed]/50 transition-all flex items-center justify-center gap-2"
                  data-testid="book-call-section-cta"
                >
                  See if you qualify
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-white/30 text-xs text-center mt-4">
              Before you book: no free trials or discounts, no account management, no guaranteed returns, no monthly plan.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookCallSection;
